import SortOrders from "./sort-orders";
import ServiceUtils from "./service-utils";
import Parameter from "./parameter";
import Msg from "./message";
import Feedback from "./feedback";
import Entity from "./entity";
import EmailDraft from "./email-draft";
import { clone, isValidEmail, sortByOrder } from "../utils";
import IEntity from "../interface/entity";
import IEmailTemplate from "../interface/email-template";
import ServiceName from "../enum/service-name";
import ParamStorage from "../enum/param-storage";
import Message from "../enum/message";
import ErrorCode from "../enum/error-code";
import EmailTemplateType from "../enum/email-template-type";
import ParameterStorage from "../class/parameter-storage";

class EmailTemplateInstance {

    private readonly Store = ParamStorage.EmailTpl;

    /* Public Methods */

    public getAll(entityId: number | null = null): IEmailTemplate[] {
        const all: IEmailTemplate[] = this.getStore().getAll().filter((tpl: IEmailTemplate) => {
            return tpl.type === EmailTemplateType.Regular;
        });
        if (entityId) {
            const order: number[] = SortOrders.get(this.Store, entityId);
            const result: IEmailTemplate[] = all.filter((tpl: IEmailTemplate) => tpl.entityId === entityId);
            return sortByOrder(result, 'id', order);
        }
        return all;
    }

    public get(id: number): IEmailTemplate | null {
        const tpl: IEmailTemplate | null = this.getStore().get(id);
        if (tpl) {
            return this.prepareTemplate(tpl);
        }
        return tpl;
    }

    public async create(tpl: IEmailTemplate): Promise<IEmailTemplate> {
        const result: IEmailTemplate = await this.getStore().create(this.validate(this.toRaw(tpl)));
        return this.get(result.id as number) as IEmailTemplate;
    }

    public async update(tpl: IEmailTemplate): Promise<IEmailTemplate> {
        const result: IEmailTemplate = await this.getStore().update(this.validate(this.toRaw(tpl)));
        return this.get(result.id as number) as IEmailTemplate;
    }

    public async delete(id: number): Promise<number> {
        const tpl: IEmailTemplate | null = this.get(id);
        if (tpl) {
            await Feedback.renameKeysToFixed(ServiceName.Email, tpl);
            return await this.getStore().delete(id);
        }
        return id;
    }

    public async clone(id: number): Promise<IEmailTemplate | null> {
        const tpl: IEmailTemplate | null = await this.load(id);
        if (tpl) {
            const cloneTpl: IEmailTemplate = clone(tpl);
            delete cloneTpl.id;
            cloneTpl.name = `${Msg.get(Message.copy)} ${cloneTpl.name}`;
            return await this.create(cloneTpl);
        }
        return null;
    }

    public async load(id: number): Promise<IEmailTemplate | null> {
        const tpl: IEmailTemplate | null = await this.getStore().load(id);
        if (tpl && tpl.body) {
            return this.prepareTemplate(tpl);
        }
        return tpl;
    }

    public async setOrder(entityId: number | null = null, order: number[]): Promise<IEmailTemplate[]> {
        await SortOrders.set({ param: this.Store, entityId, order });
        return this.getAll(entityId);
    }

    /* Private Public Methods */

    public async bulk(toCreate: IEmailTemplate[], toUpdate: IEmailTemplate[], toDelete: number[]): Promise<IEmailTemplate[]> {
        const create: IEmailTemplate[] = toCreate.map((t: IEmailTemplate) => this.validate(this.toRaw(t)));
        const update: IEmailTemplate[] = toUpdate.map((t: IEmailTemplate) => this.validate(this.toRaw(t)));
        return await this.getStore().bulk(create, update, toDelete);
    }

    public async validateAndSave(tpl: IEmailTemplate, save: boolean): Promise<IEmailTemplate> {
        if (save) {
            if (tpl.id) {
                tpl = await this.update(tpl);
            } else {
                tpl = await this.create(tpl);
            }
        }
        return this.validate(this.toRaw(tpl));
    }

    /* Private Methods */

    private validate(tpl: IEmailTemplate): IEmailTemplate {
        if (!isValidEmail(tpl.fromEmail)) {
            throw ErrorCode.EmailInvalidSenderAddress;
        } else if (!tpl.body) {
            throw ErrorCode.EmailInvalidMessageEmpty;
        } else if (!ServiceUtils.validPath(tpl.emissionPath)) {
            throw ErrorCode.InvalidPathId;
        } else {
            const entity: IEntity | null = Entity.get(tpl.entityId);
            let valid: boolean = !!entity;
            if (tpl.type === EmailTemplateType.Regular) {
                valid = valid && !!tpl.name;
            }
            valid = valid && !!tpl.subject;
            valid = valid && !!tpl.fromName;
            if (!valid) {
                throw ErrorCode.BadArguments;
            }
        }
        return tpl;
    }

    private toRaw(original: IEmailTemplate): IEmailTemplate {
        const tpl: IEmailTemplate = clone(original);
        tpl.body = ServiceUtils.replaceTagsToId(tpl.body || '');
        tpl.body = ServiceUtils.replaceSignsToId(tpl.body || '');
        tpl.body = ServiceUtils.replaceSubsToId(tpl.entityId, tpl.body || '');
        tpl.body = EmailDraft.replaceDraftToRaw(tpl.body || '');
        tpl.subject = ServiceUtils.replaceSubsToId(tpl.entityId, tpl.subject);
        tpl.cc = tpl.cc.filter((address: string) => !!address);
        tpl.bcc = tpl.bcc.filter((address: string) => !!address);
        /* Trimming all string data */
        tpl.name = tpl.name.trim();
        tpl.subject = tpl.subject.trim();
        tpl.fromName = tpl.fromName.trim();
        tpl.fromEmail = tpl.fromEmail.trim();
        tpl.body = tpl.body.trim();
        tpl.cc = tpl.cc.map((cc: string) => cc.trim());
        tpl.bcc = tpl.bcc.map((bcc: string) => bcc.trim());
        return tpl;
    }

    private prepareTemplate(tpl: IEmailTemplate): IEmailTemplate {
        tpl.subject = ServiceUtils.replaceSubsToName(tpl.entityId, tpl.subject);
        tpl.cc = tpl.cc.filter((address: string) => !!address);
        tpl.bcc = tpl.bcc.filter((address: string) => !!address);
        if (tpl.body) {
            tpl.body = ServiceUtils.replaceTagsToName(tpl.body);
            tpl.body = ServiceUtils.replaceSubsToName(tpl.entityId, tpl.body);
            tpl.body = EmailDraft.replaceRawToDraft(tpl.body);
        }
        return tpl;
    }

    private getStore(): ParameterStorage<IEmailTemplate> {
        return Parameter.get(this.Store);
    }

}

const EmailTemplate = new EmailTemplateInstance();

export default EmailTemplate;